// views/Dashboard/PainelExecutivo/sections/comercial/useComercialDashboardData.ts
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../../../src/lib/supabase';
import { embedToArray } from '../../../../../src/lib/utils';
import type { Profile } from '../../../../../types';
import type { EventFunnelRow } from '../../../../CRM/Analytics/eventFunnel';

// `cliente_nome`, `cpf_cnpj`, `cidade`, `tipo_cliente` e `origem` não entram em
// conta nenhuma: são o que a lista mostra quando alguém clica no card do funil.
const COLUNAS = 'id, status, consumo_medio_kwh, responsavel_id, created_at, data_assinatura_termo, url_simulacao, url_termo, '
  + 'cliente_nome, cpf_cnpj, cidade, tipo_cliente, origem, analise_cadastral(decisao_final, decisao_final_at)';
// #6331: data e motivo da reprovação. Separado porque as colunas podem ainda não
// existir no banco — aí a carga volta sem elas e só o card "Termo Reprovado" fica zerado.
const COLUNAS_REPROVACAO = ', termo_reprovado_em, termo_reprovado_motivo, termo_reprovado_aproximado';
const PAGE = 1000;

// Busca paginada: o Supabase/PostgREST limita cada resposta a 1000 linhas, e a
// base de negociações já passa disso — uma query única truncaria o total de
// kWh silenciosamente (mesmo cuidado já tomado em AnalyticsComercialView.tsx).
export async function buscarTodasAsNegociacoes(): Promise<EventFunnelRow[]> {
  const todas: EventFunnelRow[] = [];
  let colunas = COLUNAS + COLUNAS_REPROVACAO;
  for (let page = 0; ; page++) {
    const from = page * PAGE;
    let { data, error } = await supabase
      .from('negociacoes')
      .select(colunas)
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error && colunas !== COLUNAS && /termo_reprovado/.test(error.message || '')) {
      colunas = COLUNAS;
      ({ data, error } = await supabase.from('negociacoes').select(colunas).order('id', { ascending: true }).range(from, from + PAGE - 1));
    }
    if (error) throw error;
    const batch = (data || []) as any[];
    // embed 1→1 (UNIQUE em negociacao_id) vem como objeto — normaliza pra
    // array, mesmo padrão de AnalyticsComercialView.tsx/KanbanBoard.tsx.
    batch.forEach((n) => { n.analise_cadastral = embedToArray(n.analise_cadastral); });
    todas.push(...(batch as EventFunnelRow[]));
    if (batch.length < PAGE) break;
  }
  return todas;
}

/**
 * Última carga, compartilhada entre o bloco Comercial da Visão geral e a aba
 * Comercial — os dois usam este hook, e sem o cache a lista inteira de
 * negociações (paginada de 1.000 em 1.000) era buscada de novo a cada troca de
 * aba. 10 minutos, o mesmo fôlego do painel financeiro.
 */
const VALIDADE_MS = 10 * 60 * 1000;
let cache: { dados: EventFunnelRow[]; profiles: Map<string, Profile>; quando: number } | null = null;
const cacheValido = (): boolean => !!cache && Date.now() - cache.quando < VALIDADE_MS;

/** Busca em andamento, compartilhada: dois consumidores montados juntos não baixam as negociações duas vezes. */
let emAndamento: Promise<void> | null = null;

async function buscarECachear(): Promise<void> {
  const [negociacoes, { data: profilesData, error: profilesErr }] = await Promise.all([
    buscarTodasAsNegociacoes(),
    supabase.from('profiles').select('*').neq('role', 'consumidor'),
  ]);
  if (profilesErr) throw profilesErr;
  const map = new Map<string, Profile>();
  ((profilesData || []) as Profile[]).forEach((p) => map.set(p.id, p));
  cache = { dados: negociacoes, profiles: map, quando: Date.now() };
}

export function useComercialDashboardData() {
  const [dados, setDados] = useState<EventFunnelRow[]>(() => (cacheValido() ? cache!.dados : []));
  const [profilesById, setProfilesById] = useState<Map<string, Profile>>(
    () => (cacheValido() ? cache!.profiles : new Map()),
  );
  const [loading, setLoading] = useState(!cacheValido());
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (forcar = true) => {
    if (!forcar && cacheValido()) {
      setDados(cache!.dados);
      setProfilesById(cache!.profiles);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      if (!emAndamento) emAndamento = buscarECachear().finally(() => { emAndamento = null; });
      await emAndamento;
      setDados(cache!.dados);
      setProfilesById(cache!.profiles);
    } catch (e: any) {
      console.error('Erro ao carregar seção Comercial do Painel Executivo:', e);
      setErro(e?.message || 'Erro ao carregar os dados comerciais.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar(false);
  }, [carregar]);

  return { dados, profilesById, loading, erro, refetch: carregar };
}
